import { IconButton } from '@components/core/icon-button.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { challengeFormOptions } from '../challenge-form-options';
import { CriteriaBuilder } from './criteria-builder.component';

/**
 * Third step of challenge creation form
 * Configures phases with names and inline phase-specific criteria
 */
export const ChallengePhasesStep = withForm({
  ...challengeFormOptions,
  render: function Render({ form }) {
    const [expandedPhases, setExpandedPhases] = useState<string[]>(['0']);

    return (
      <form.AppForm>
        <form.AppField
          mode="array"
          name="challengePhases"
        >
          {(field) => (
            <div className="space-y-4">
              {field.state.value.length === 0 ? (
                <EmptyStateContainer
                  message="Add Phase"
                  onClick={() => {
                    field.pushValue({ criteria: [], name: '' });
                    setExpandedPhases(['0']);
                  }}
                  title="No Phases Added"
                />
              ) : (
                <>
                  <Accordion
                    onValueChange={setExpandedPhases}
                    type="multiple"
                    value={expandedPhases}
                  >
                    {field.state.value.map((_, index) => {
                      return (
                        <AccordionItem
                          className="border-b-0 mb-4 last:mb-0"
                          key={index}
                          value={String(index)}
                        >
                          <div className="border rounded-lg p-4 bg-white">
                            {/* Phase Header */}
                            <div className="flex items-center gap-2 justify-between">
                              <div className="flex-1">
                                <form.AppField name={`challengePhases[${index}].name`}>
                                  {(nameField) => (
                                    <nameField.TextField
                                      label="Name"
                                      placeholder="Enter phase name"
                                      required
                                    />
                                  )}
                                </form.AppField>
                              </div>

                              <IconButton
                                className="mt-[22px]"
                                ariaLabel="Remove phase"
                                icon={<Trash2Icon size={16} />}
                                onClick={() => {
                                  field.removeValue(index);
                                  setExpandedPhases((prev) => prev.filter((i) => i !== String(index)));
                                }}
                                size="lg"
                                variant="secondary"
                              />
                            </div>

                            <form.Subscribe selector={(state) => state.values.challengePhases[index].criteria.length}>
                              {(criteriaCount) => (
                                <AccordionTrigger className="mt-4 p-0 w-auto hover:no-underline grow-0">
                                  <span className="text-sm font-normal mr-1 whitespace-nowrap">
                                    {criteriaCount > 0
                                      ? `${criteriaCount} ${criteriaCount === 1 ? 'criterion' : 'criteria'}`
                                      : 'No criteria'}
                                  </span>
                                </AccordionTrigger>
                              )}
                            </form.Subscribe>

                            {/* Phase Criteria Section (Accordion) */}
                            <AccordionContent className="p-0">
                              <div className="py-4">
                                <CriteriaBuilder
                                  form={form}
                                  position={index}
                                />
                              </div>
                            </AccordionContent>
                          </div>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>

                  <Button
                    className="h-auto mt-4"
                    onClick={() => {
                      const newIndex = field.state.value.length;
                      field.pushValue({ criteria: [], name: '' });
                      setExpandedPhases([...expandedPhases, String(newIndex)]);
                    }}
                    variant="link"
                  >
                    + Add Phase
                  </Button>
                </>
              )}
            </div>
          )}
        </form.AppField>
      </form.AppForm>
    );
  },
});
